import React, { useState } from 'react';
import { useNotesSupabase as useNotes } from './hooks/useNotesSupabase';
import NotesListView from './NotesListView';

/**
 * Composant centralisé pour gérer toutes les notes
 * Peut être utilisé dans n'importe quelle page avec un contexte spécifique
 */
const NotesManager = ({ context = null, bibleReference = null, onClose = null }) => {
  const { notes, loading, error, createNote, updateNote, deleteNote, searchNotes, exportNotes } = useNotes(context);

  const [isCreating, setIsCreating] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showListView, setShowListView] = useState(false);
  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    tags: [],
    bible_reference: bibleReference || ''
  });

  // Gérer la création d'une note
  const handleCreateNote = async () => {
    if (!newNote.title.trim() || !newNote.content.trim()) {
      alert('Titre et contenu sont obligatoires');
      return;
    }

    try {
      await createNote(newNote);
      setNewNote({
        title: '',
        content: '',
        tags: [],
        bible_reference: bibleReference || ''
      });
      setIsCreating(false);
      alert('✅ Note créée avec succès !');
    } catch (err) {
      alert(`❌ Erreur: ${err.message}`);
    }
  };

  // Gérer la mise à jour d'une note
  const handleUpdateNote = async () => {
    if (!editingNote) return;

    try {
      // Utiliser _id comme identifiant
      const noteId = editingNote.id || editingNote._id;
      await updateNote(noteId, {
        title: editingNote.title,
        content: editingNote.content,
        tags: editingNote.tags,
        bible_reference: editingNote.bible_reference
      });
      setEditingNote(null);
      alert('✅ Note mise à jour !');
    } catch (err) {
      alert(`❌ Erreur: ${err.message}`);
    }
  };

  // Gérer la suppression d'une note
  const handleDeleteNote = async (note) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette note ?')) {
      return;
    }

    try {
      const noteId = note.id || note._id;
      await deleteNote(noteId);
      alert('✅ Note supprimée !');
    } catch (err) {
      alert(`❌ Erreur: ${err.message}`);
    }
  };

  // Gérer la recherche
  const handleSearch = async () => {
    if (searchTerm.trim()) {
      await searchNotes(searchTerm);
    }
  };

  // Gérer l'export
  const handleExport = async () => {
    try {
      await exportNotes();
      alert('✅ Notes exportées avec succès !');
    } catch (err) {
      alert(`❌ Erreur export: ${err.message}`);
    }
  };

  // Styles inline
  const containerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    padding: '20px'
  };

  const modalStyle = {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '900px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
  };

  const headerStyle = {
    padding: '20px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  const contentStyle = {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  };

  const buttonStyle = {
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.3s'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#667eea',
    color: 'white'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#e0e0e0',
    color: '#333'
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#ef4444',
    color: 'white'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    borderRadius: '6px',
    border: '1px solid #d0d0d0',
    fontSize: '14px',
    marginBottom: '10px'
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '150px',
    fontFamily: 'inherit',
    resize: 'vertical'
  };

  const noteCardStyle = {
    backgroundColor: '#f9f9f9',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '10px',
    border: '1px solid #e0e0e0'
  };

  return (
    <div style={containerStyle} onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: '24px', color: '#333' }}>
            📝 Mes Notes {context && `- ${context}`}
          </h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={secondaryButtonStyle} onClick={() => setShowListView(true)}>
              📋 Liste des notes
            </button>
            <button style={secondaryButtonStyle} onClick={handleExport}>
              📤 Exporter
            </button>
            <button style={primaryButtonStyle} onClick={() => setIsCreating(true)}>
              ➕ Nouvelle Note
            </button>
            {onClose && (
              <button style={secondaryButtonStyle} onClick={onClose}>
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {/* Barre de recherche */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <input
              type="text"
              placeholder="Rechercher dans les notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(); }}
              style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
            />
            <button style={primaryButtonStyle} onClick={handleSearch}>
              🔍 Rechercher
            </button>
          </div>

          {/* Formulaire de création */}
          {isCreating && (
            <div style={{ ...noteCardStyle, backgroundColor: '#f0f4ff', marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 15px 0' }}>Créer une nouvelle note</h3>
              <input
                type="text"
                placeholder="Titre de la note *"
                value={newNote.title}
                onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                style={inputStyle}
              />
              <textarea
                placeholder="Contenu de la note *"
                value={newNote.content}
                onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                style={textareaStyle}
              />
              <input
                type="text"
                placeholder="Référence biblique (ex: Jean 3:16)"
                value={newNote.bible_reference}
                onChange={(e) => setNewNote({ ...newNote, bible_reference: e.target.value })}
                style={inputStyle}
              />
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button style={primaryButtonStyle} onClick={handleCreateNote}>
                  💾 Enregistrer
                </button>
                <button
                  style={secondaryButtonStyle}
                  onClick={() => {
                    setIsCreating(false);
                    setNewNote({ title: '', content: '', tags: [], bible_reference: bibleReference || '' });
                  }}
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {/* Formulaire d'édition */}
          {editingNote && (
            <div style={{ ...noteCardStyle, backgroundColor: '#fff8e1', marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 15px 0' }}>Modifier la note</h3>
              <input
                type="text"
                value={editingNote.title}
                onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
                style={inputStyle}
              />
              <textarea
                value={editingNote.content}
                onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
                style={textareaStyle}
              />
              <input
                type="text"
                placeholder="Référence biblique"
                value={editingNote.bible_reference || ''}
                onChange={(e) => setEditingNote({ ...editingNote, bible_reference: e.target.value })}
                style={inputStyle}
              />
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button style={primaryButtonStyle} onClick={handleUpdateNote}>
                  💾 Sauvegarder
                </button>
                <button style={secondaryButtonStyle} onClick={() => setEditingNote(null)}>
                  Annuler
                </button>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              Chargement...
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ padding: '15px', backgroundColor: '#fee', color: '#c00', borderRadius: '6px', marginBottom: '15px' }}>
              ❌ Erreur: {error}
            </div>
          )}

          {/* Liste des notes */}
          {!loading && notes.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              Aucune note. Créez votre première note !
            </div>
          )}

          {!loading && notes.length > 0 && (
            <div>
              <h3 style={{ marginBottom: '15px', color: '#666' }}>
                {notes.length} note{notes.length > 1 ? 's' : ''}
              </h3>
              {notes.map((note) => (
                <div key={note.id || note._id} style={noteCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, fontSize: '18px', color: '#333' }}>{note.title}</h4>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        style={{ ...buttonStyle, padding: '5px 10px', fontSize: '12px', backgroundColor: '#667eea', color: 'white' }}
                        onClick={() => setEditingNote(note)}
                      >
                        ✏️ Modifier
                      </button>
                      <button
                        style={{ ...buttonStyle, padding: '5px 10px', fontSize: '12px', backgroundColor: '#ef4444', color: 'white' }}
                        onClick={() => handleDeleteNote(note)}
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  </div>

                  <p style={{ margin: '10px 0', color: '#555', whiteSpace: 'pre-wrap' }}>
                    {note.content}
                  </p>

                  {note.bible_reference && (
                    <div style={{ fontSize: '12px', color: '#667eea', marginTop: '10px' }}>
                      📖 {note.bible_reference}
                    </div>
                  )}

                  {note.context && (
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                      📁 Contexte: {note.context}
                    </div>
                  )}

                  <div style={{ fontSize: '11px', color: '#aaa', marginTop: '10px', borderTop: '1px solid #e0e0e0', paddingTop: '10px' }}>
                    Créée le: {new Date(note.created_at).toLocaleString('fr-FR')}
                    {note.updated_at !== note.created_at && (
                      <> • Modifiée le: {new Date(note.updated_at).toLocaleString('fr-FR')}</>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Vue Liste des Notes */}
      {showListView && (
        <NotesListView
          notes={notes}
          onClose={() => setShowListView(false)}
          onUpdateNote={updateNote}
          onSave={() => {
            alert('✅ Toutes les modifications ont été sauvegardées !');
            setShowListView(false);
          }}
        />
      )}
    </div>
  );
};

export default NotesManager;
